import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { adminCinemaSectors, adminSaveSectors } from '../../api/endpoints';
import { haptic, lockMiniAppViewport, showAlert, unlockMiniAppViewport } from '../../lib/tg';
import { usePinchZoom } from '../../hooks/usePinchZoom';
import TopBar from '../../components/TopBar';
import Splash from '../../components/Splash';
import type { Sector } from '../../types/models';

const PALETTE = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444'];
const CELL = 16;
const GAP = 3;
const STRIDE = CELL + GAP; // one cell-width = one snap step
const MIN_ZOOM = 0.4;
const MAX_ZOOM = 2.0;
const ZOOM_STEP = 0.15;

function snap(v: number): number {
  return Math.max(0, Math.round(v / STRIDE) * STRIDE);
}

function sectorRect(s: { pos_x: number; pos_y: number; seats_per_row: number; rows_count: number }) {
  return {
    x1: s.pos_x,
    y1: s.pos_y,
    x2: s.pos_x + s.seats_per_row * STRIDE,
    y2: s.pos_y + s.rows_count * STRIDE,
  };
}

function rectsOverlap(a: ReturnType<typeof sectorRect>, b: ReturnType<typeof sectorRect>) {
  return !(a.x2 <= b.x1 || b.x2 <= a.x1 || a.y2 <= b.y1 || b.y2 <= a.y1);
}

function findOverlaps(sectors: Sector[]): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = 0; i < sectors.length; i++) {
    for (let j = i + 1; j < sectors.length; j++) {
      if (rectsOverlap(sectorRect(sectors[i]), sectorRect(sectors[j]))) out.push([i, j]);
    }
  }
  return out;
}

export default function SeatEditor() {
  const { id } = useParams();
  const { data, isLoading } = useQuery({
    queryKey: ['cinema-sectors', id],
    queryFn: () => adminCinemaSectors(id!),
    enabled: !!id,
  });

  const [sectors, setSectors] = useState<Sector[]>([]);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  // Shared flag: while a pinch is in progress, suppress drag-from-sector logic
  // so the gesture doesn't fight with sector long-press handlers.
  const pinchingRef = useRef({ current: false });

  useEffect(() => { if (data) setSectors(data); }, [data]);

  // Center the canvas horizontally on the 'screen' marker after layout.
  useEffect(() => {
    if (!sectors.length) return;
    const el = scrollRef.current;
    if (!el) return;
    const canvasW = Math.max(
      900,
      ...sectors.map((s) => s.pos_x + s.seats_per_row * STRIDE + 60),
    );
    requestAnimationFrame(() => {
      el.scrollLeft = Math.max(0, (canvasW * zoom - el.clientWidth) / 2);
    });
    // Only on first load / sector data change, not on every drag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Lock vertical swipes so dragging doesn't yank the Mini App sheet.
  useEffect(() => {
    lockMiniAppViewport();
    return () => unlockMiniAppViewport();
  }, []);

  const overlaps = findOverlaps(sectors);
  const hasOverlap = overlaps.length > 0;

  const saveMut = useMutation({
    mutationFn: () => adminSaveSectors(id!, sectors),
    onSuccess: () => showAlert('Схема сохранена'),
    onError: (e: any) => {
      const detail = e?.response?.data?.detail;
      showAlert(typeof detail === 'string' && detail ? detail : 'Ошибка сохранения');
    },
  });

  function onSave() {
    if (hasOverlap) {
      const names = new Set<string>();
      overlaps.forEach(([i, j]) => {
        names.add(sectors[i].name);
        names.add(sectors[j].name);
      });
      showAlert(`Секторы пересекаются: ${[...names].join(', ')}. Разнесите их и попробуйте снова.`);
      return;
    }
    saveMut.mutate();
  }

  function addSector() {
    haptic('light');
    const el = scrollRef.current;
    const baseX = el ? el.scrollLeft / zoom + 20 : 20;
    const baseY = el ? el.scrollTop / zoom + 40 : 40;
    setSectors((s) => {
      const proposed: Sector = {
        name: `Сектор ${s.length + 1}`,
        color: PALETTE[s.length % PALETTE.length],
        rows_count: 5,
        seats_per_row: 8,
        pos_x: snap(baseX),
        pos_y: snap(baseY),
      };
      // If the spawn point overlaps an existing sector, shift down in snap
      // steps until we find a free slot.
      let attempt = 0;
      while (
        attempt < 50 &&
        s.some((other) => rectsOverlap(sectorRect(proposed), sectorRect(other)))
      ) {
        proposed.pos_y = snap(proposed.pos_y + STRIDE * 2);
        attempt++;
      }
      return [...s, proposed];
    });
  }

  // Canvas size: enough to contain all sectors + padding.
  const canvasW = Math.max(
    900,
    ...sectors.map((s) => s.pos_x + s.seats_per_row * (CELL + GAP) + 60),
  );
  const canvasH = Math.max(
    520,
    ...sectors.map((s) => s.pos_y + s.rows_count * (CELL + GAP) + 80),
  );

  usePinchZoom({
    container: scrollRef,
    outer: outerRef,
    inner: innerRef,
    baseWidth: canvasW,
    baseHeight: canvasH,
    zoom,
    setZoom: (z) => setZoom(+z.toFixed(3)),
    minZoom: MIN_ZOOM,
    maxZoom: MAX_ZOOM,
    pinchingRef,
  });

  return (
    <div className="min-h-screen pb-40">
      <TopBar title="Схема зала" />
      <div className="px-4 pt-2 pb-2 text-xs text-muted">
        Long-press — перетаскивание. Короткий тап — редактировать. Секторы с одинаковыми именами объединяются в одну ценовую категорию.
      </div>

      <div className="flex items-center justify-end gap-2 px-3 pb-2">
        <button
          className="px-3 py-1.5 rounded-full bg-bg-elev text-sm disabled:opacity-50"
          disabled={zoom <= MIN_ZOOM + 1e-3}
          onClick={() => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)))}
        >
          −
        </button>
        <div className="text-xs text-muted w-10 text-center">{Math.round(zoom * 100)}%</div>
        <button
          className="px-3 py-1.5 rounded-full bg-bg-elev text-sm disabled:opacity-50"
          disabled={zoom >= MAX_ZOOM - 1e-3}
          onClick={() => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)))}
        >
          +
        </button>
        <button
          className="px-3 py-1.5 rounded-full bg-bg-elev text-sm"
          onClick={() => setZoom(1)}
        >
          1:1
        </button>
      </div>

      <div
        ref={scrollRef}
        className="mx-3 bg-bg-elev rounded-xl2 overflow-auto no-drag-select"
        style={{ height: 520, touchAction: 'pan-x pan-y' }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div
          ref={outerRef}
          className="relative"
          style={{
            // The scaled canvas takes up zoom × intrinsic size, so the scroll
            // container has room to pan in both axes. Width/height are mutated
            // imperatively during pinch.
            width: canvasW * zoom,
            height: canvasH * zoom,
          }}
        >
          <div
            ref={innerRef}
            className="absolute top-0 left-0"
            style={{
              width: canvasW,
              height: canvasH,
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
            }}
          >
            <div className="absolute top-2 left-0 right-0 text-center text-[10px] text-muted">— ЭКРАН —</div>
            {sectors.map((sec, i) => {
              const overlapping = overlaps.some(([a, b]) => a === i || b === i);
              return (
                <DraggableSector
                  key={i}
                  sector={sec}
                  zoom={zoom}
                  overlap={overlapping}
                  pinchingRef={pinchingRef}
                  onChange={(s) => setSectors((arr) => arr.map((x, j) => (j === i ? s : x)))}
                  onTap={() => setEditingIdx(i)}
                />
              );
            })}
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 p-4 bg-bg border-t border-line grid grid-cols-2 gap-2">
        <button className="btn-secondary" onClick={addSector}>+ Сектор</button>
        <button className="btn-primary" disabled={saveMut.isPending || hasOverlap} onClick={onSave}>
          {hasOverlap ? 'Сектора пересекаются' : 'Сохранить'}
        </button>
      </div>

      {editingIdx !== null && (
        <SectorEditModal
          sector={sectors[editingIdx]}
          onClose={() => setEditingIdx(null)}
          onSave={(s) => {
            setSectors((arr) => arr.map((x, j) => (j === editingIdx ? s : x)));
            setEditingIdx(null);
          }}
          onDelete={() => {
            setSectors((arr) => arr.filter((_, j) => j !== editingIdx));
            setEditingIdx(null);
          }}
        />
      )}

      {isLoading && <Splash />}
    </div>
  );
}

function DraggableSector({
  sector,
  zoom,
  overlap,
  pinchingRef,
  onChange,
  onTap,
}: {
  sector: Sector;
  zoom: number;
  overlap: boolean;
  pinchingRef?: React.RefObject<{ current: boolean }>;
  onChange: (s: Sector) => void;
  onTap: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const longPressTimer = useRef<number | null>(null);
  const startPos = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const moved = useRef(false);

  function cancelLongPress() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  function onPointerDown(e: React.PointerEvent) {
    // If a pinch is already underway, ignore — the gesture belongs to the
    // canvas, not to a sector drag.
    if (pinchingRef?.current?.current) return;
    moved.current = false;
    startPos.current = { x: e.clientX, y: e.clientY, px: sector.pos_x, py: sector.pos_y };
    longPressTimer.current = window.setTimeout(() => {
      setDragging(true);
      haptic('medium');
    }, 350);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!startPos.current) return;
    // A second finger landed → cancel any pending drag; the pinch handler
    // will take over the gesture.
    if (pinchingRef?.current?.current) {
      cancelLongPress();
      setDragging(false);
      startPos.current = null;
      return;
    }
    // Pointer deltas are in screen pixels; canvas coords are unzoomed.
    const dx = (e.clientX - startPos.current.x) / zoom;
    const dy = (e.clientY - startPos.current.y) / zoom;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) moved.current = true;
    if (dragging) {
      // Live snap-to-grid so the sector visibly clicks into place while the
      // user is still dragging.
      onChange({
        ...sector,
        pos_x: snap(Math.max(0, startPos.current.px + dx)),
        pos_y: snap(Math.max(0, startPos.current.py + dy)),
      });
    }
  }

  function onPointerUp() {
    cancelLongPress();
    if (!dragging && !moved.current && !pinchingRef?.current?.current) onTap();
    setDragging(false);
    startPos.current = null;
  }

  return (
    <div
      ref={ref}
      className="no-drag-select"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      style={{
        position: 'absolute',
        left: sector.pos_x,
        top: sector.pos_y,
        outline: dragging
          ? '2px dashed #fff'
          : overlap
            ? '2px solid #ef4444'
            : 'none',
        padding: 4,
        borderRadius: 8,
      }}
    >
      <div className="text-[10px] mb-1" style={{ color: sector.color }}>{sector.name}</div>
      <div
        className="grid"
        style={{ gridTemplateColumns: `repeat(${sector.seats_per_row}, ${CELL}px)`, gap: GAP }}
      >
        {Array.from({ length: sector.rows_count * sector.seats_per_row }).map((_, k) => (
          <div key={k} style={{ width: CELL, height: CELL, background: sector.color, borderRadius: 3 }} />
        ))}
      </div>
    </div>
  );
}

function SectorEditModal({
  sector,
  onSave,
  onClose,
  onDelete,
}: {
  sector: Sector;
  onSave: (s: Sector) => void;
  onClose: () => void;
  onDelete: () => void;
}) {
  const [draft, setDraft] = useState(sector);
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end" onClick={onClose}>
      <div className="bg-bg-elev w-full rounded-t-xl2 p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Сектор</h3>
          <button onClick={onClose} className="text-muted">✕</button>
        </div>
        <div>
          <label className="label">Название</label>
          <input className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">Рядов</label>
            <input className="input" type="number" min={1} max={30}
              value={draft.rows_count === 0 ? '' : draft.rows_count}
              onChange={(e) => {
                const v = e.target.value;
                setDraft({ ...draft, rows_count: v === '' ? 0 : Number(v) });
              }}
              onBlur={(e) => {
                const n = Number(e.target.value);
                setDraft({ ...draft, rows_count: Math.min(30, Math.max(1, isNaN(n) ? 1 : n)) });
              }} />
          </div>
          <div>
            <label className="label">Мест в ряду</label>
            <input className="input" type="number" min={1} max={30}
              value={draft.seats_per_row === 0 ? '' : draft.seats_per_row}
              onChange={(e) => {
                const v = e.target.value;
                setDraft({ ...draft, seats_per_row: v === '' ? 0 : Number(v) });
              }}
              onBlur={(e) => {
                const n = Number(e.target.value);
                setDraft({ ...draft, seats_per_row: Math.min(30, Math.max(1, isNaN(n) ? 1 : n)) });
              }} />
          </div>
        </div>
        <div>
          <label className="label">Цвет</label>
          <div className="flex gap-2 flex-wrap">
            {PALETTE.map((c) => (
              <button
                key={c}
                onClick={() => setDraft({ ...draft, color: c })}
                style={{ background: c, width: 36, height: 36, borderRadius: 10, border: draft.color === c ? '2px solid #fff' : '2px solid transparent' }}
              />
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-2">
          <button className="btn-secondary" onClick={onDelete}>Удалить</button>
          <button className="btn-primary" onClick={() => onSave({
            ...draft,
            rows_count: Math.min(30, Math.max(1, draft.rows_count || 1)),
            seats_per_row: Math.min(30, Math.max(1, draft.seats_per_row || 1)),
          })}>Готово</button>
        </div>
      </div>
    </div>
  );
}
